import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  fetchFleetSubscriptions,
  fetchCurrentPricing,
  fetchPricingHistory,
  updatePricing,
  activateSubscription,
  fetchUserSubscriptionHistory,
  exportSubscriptionsCSV,
  fetchFleetCriteria,
  updateFleetCriteria,
} from '../../../services/fleetSubscriptionService';
import type {
  SubscriptionFilters,
  ActivateSubscriptionRequest,
  UpdatePricingRequest,
  UpdateCriteriaRequest,
} from '../types/subscription.types';

/**
 * Hook to fetch fleet subscriptions with filters
 */
export const useFleetSubscriptions = (filters: SubscriptionFilters = {}) => {
  return useQuery({
    queryKey: ['fleet-subscriptions', filters],
    queryFn: () => fetchFleetSubscriptions(filters),
    staleTime: 30000, // 30 seconds
    retry: 2,
  });
};

/**
 * Hook to fetch current pricing configuration
 */
export const useCurrentPricing = () => {
  return useQuery({
    queryKey: ['fleet-pricing'],
    queryFn: fetchCurrentPricing,
    staleTime: 60000, // 1 minute
    retry: 2,
  });
};

/**
 * Hook to fetch pricing history
 */
export const usePricingHistory = () => {
  return useQuery({
    queryKey: ['fleet-pricing-history'],
    queryFn: fetchPricingHistory,
    staleTime: 60000, // 1 minute
    retry: 2,
  });
};

/**
 * Hook to fetch user subscription history
 */
export const useUserSubscriptionHistory = (userId: number | null) => {
  return useQuery({
    queryKey: ['user-subscription-history', userId],
    queryFn: () => fetchUserSubscriptionHistory(userId!),
    enabled: !!userId,
    staleTime: 30000,
    retry: 2,
  });
};

/**
 * Hook to activate a subscription
 */
export const useActivateSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: number;
      data: ActivateSubscriptionRequest;
    }) => activateSubscription(userId, data),
    onSuccess: (response) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['fleet-subscriptions'] });
      queryClient.invalidateQueries({
        queryKey: ['user-subscription-history', response.user.user_id],
      });

      toast.success(response.message || 'Subscription activated successfully!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to activate subscription');
    },
  });
};

/**
 * Hook to update pricing
 */
export const useUpdatePricing = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdatePricingRequest) => updatePricing(data),
    onSuccess: (response) => {
      // Invalidate pricing queries
      queryClient.invalidateQueries({ queryKey: ['fleet-pricing'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-pricing-history'] });

      toast.success(response.message || 'Pricing updated successfully!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update pricing');
    },
  });
};

/**
 * Hook to export subscriptions to CSV
 */
export const useExportSubscriptions = () => {
  return useMutation({
    mutationFn: (filters: SubscriptionFilters) => exportSubscriptionsCSV(filters),
    onSuccess: (blob) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fleet-subscriptions-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Export downloaded successfully!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to export subscriptions');
    },
  });
};

/**
 * Hook to fetch fleet criteria
 */
export const useFleetCriteria = () => {
  return useQuery({
    queryKey: ['fleet-criteria'],
    queryFn: fetchFleetCriteria,
    staleTime: 60000, // 1 minute
    retry: 2,
  });
};

/**
 * Hook to update fleet criteria
 */
export const useUpdateFleetCriteria = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateCriteriaRequest) => updateFleetCriteria(data),
    onSuccess: (response) => {
      // Invalidate criteria query
      queryClient.invalidateQueries({ queryKey: ['fleet-criteria'] });
      // Also invalidate subscriptions since criteria affects qualification
      queryClient.invalidateQueries({ queryKey: ['fleet-subscriptions'] });

      toast.success(response.message || 'Fleet criteria updated successfully!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update fleet criteria');
    },
  });
};
